import { useState, useRef, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { RealtimeChat } from '@/utils/RealtimeAudio';

export interface TranscriptMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

export const useCallMonitor = () => {
  const [isActive, setIsActive] = useState(false);
  const [transcript, setTranscript] = useState<TranscriptMessage[]>([]);
  const [userTranscript, setUserTranscript] = useState('');
  const [lastCallResults, setLastCallResults] = useState<{ notes: number; tasks: number } | null>(null);
  const chatRef = useRef<RealtimeChat | null>(null);
  const assistantBufferRef = useRef('');
  const transcriptRef = useRef<TranscriptMessage[]>([]);

  const addTranscriptMessage = useCallback((role: 'user' | 'assistant', content: string) => {
    console.log('📝 Transcript message:', { role, content });
    const newMessage = {
      id: `${Date.now()}-${Math.random()}`,
      role,
      content,
      timestamp: new Date()
    };
    setTranscript(prev => {
      const updated = [...prev, newMessage];
      transcriptRef.current = updated;
      return updated;
    });
  }, []);

  const handleMessage = useCallback((message: any) => {
    console.log('📨 Received event:', message.type);
    
    // Handle user audio transcription completed
    if (message.type === 'conversation.item.input_audio_transcription.completed') {
      const text = message.transcript || '';
      console.log('✅ User transcription completed:', text);
      if (text.trim()) {
        addTranscriptMessage('user', text);
        setUserTranscript(''); // Clear partial transcript
      }
    }
    
    // Handle partial user transcription for live updates
    if (message.type === 'input_audio_buffer.speech_started') {
      console.log('🎤 Speech started');
      setUserTranscript('');
    }
    
    if (message.type === 'conversation.item.input_audio_transcription.delta') {
      const delta = message.delta || '';
      setUserTranscript(prev => prev + delta);
    }
    
    // Handle AI responses - accumulate deltas into buffer
    if (message.type === 'response.audio_transcript.delta') {
      const delta = message.delta || '';
      assistantBufferRef.current += delta;
    }
    
    if (message.type === 'response.audio_transcript.done') {
      console.log('✅ Assistant response completed');
      const fullMessage = assistantBufferRef.current.trim();
      if (fullMessage) {
        addTranscriptMessage('assistant', fullMessage);
      }
      assistantBufferRef.current = ''; // Clear buffer
    }

    // Handle errors
    if (message.type === 'error') {
      console.error('❌ OpenAI error:', message);
      toast.error(`Error: ${message.error?.message || 'Unknown error'}`);
    }
  }, [addTranscriptMessage]);

  const startMonitoring = useCallback(async () => {
    try {
      console.log('🎬 Starting call monitoring with WebRTC...');
      
      // Clear previous state
      setTranscript([]);
      setUserTranscript('');
      transcriptRef.current = [];
      assistantBufferRef.current = '';
      
      // Create new RealtimeChat instance
      const chat = new RealtimeChat(handleMessage);
      chatRef.current = chat;
      
      // Initialize WebRTC connection
      await chat.init();
      
      setIsActive(true);
      setLastCallResults(null);
      toast.success('Call monitoring started');
      
    } catch (error) {
      console.error('❌ Failed to start monitoring:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to start monitoring';
      toast.error(errorMessage);
      setIsActive(false);
      
      // Clean up on error
      if (chatRef.current) {
        chatRef.current.disconnect();
        chatRef.current = null;
      }
    }
  }, [handleMessage]);

  const stopMonitoring = useCallback(async (
    options?: { jobId?: string; leadId?: string; mode?: string }
  ): Promise<{ notes: number; tasks: number } | null> => {
    console.log('🛑 Stopping call monitoring...');

    if (chatRef.current) {
      chatRef.current.disconnect();
      chatRef.current = null;
    }

    setIsActive(false);
    setUserTranscript('');

    // Process transcript if we have any - use ref to get latest value
    const currentTranscript = transcriptRef.current;
    console.log('📊 Processing transcript, length:', currentTranscript.length);

    let results: { notes: number; tasks: number } | null = null;

    if (currentTranscript.length > 0) {
      toast.loading('Processing call transcript...');

      try {
        const { data: sessionData } = await supabase.auth.getSession();
        const token = sessionData?.session?.access_token;

        if (!token) {
          toast.error('Session expired');
          return null;
        }

        const { data, error } = await supabase.functions.invoke('process-call-transcript', {
          body: {
            transcript: currentTranscript.map(t => ({
              role: t.role,
              content: t.content,
              timestamp: t.timestamp.toISOString()
            })),
            jobId: options?.jobId,
            leadId: options?.leadId,
            mode: options?.mode
          },
          headers: {
            Authorization: `Bearer ${token}`
          }
        });

        if (error) {
          console.error('Error processing transcript:', error);
          toast.error('Failed to process transcript');
        } else {
          const notesCount = data.results?.notes?.length || 0;
          const tasksCount = data.results?.tasks?.length || 0;
          results = { notes: notesCount, tasks: tasksCount };
          setLastCallResults(results);
          toast.success(
            `Call processed: ${notesCount} notes, ${tasksCount} tasks created`
          );
        }
      } catch (error) {
        console.error('Error:', error);
        toast.error('Failed to process call');
      }
    } else {
      toast.info('No transcript captured. Please ensure you spoke during the call.');
    }

    toast.success('Call monitoring ended');
    return results;
  }, []);

  return {
    isActive,
    transcript,
    userTranscript,
    lastCallResults,
    startMonitoring,
    stopMonitoring
  };
};
